import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Filter scope for the Reports Monitoring module. Selected in the sidebar,
 * consumed by the report-instance views. All three are optional — `null`
 * means "no filter". With no customer selected, requests run cross-tenant
 * (`X-Tenant-Id: *`) so the user sees every instance they have access to.
 *
 * Selections cascade: changing the customer resets line of business and
 * report template; changing the line of business resets the template —
 * the narrower filters are only meaningful inside the broader scope.
 */
interface ReportsFilterContextValue {
  tenantId: number | null;
  lineOfBizId: number | null;
  reportTemplateId: number | null;
  setTenantId: (id: number | null) => void;
  setLineOfBizId: (id: number | null) => void;
  setReportTemplateId: (id: number | null) => void;
}

const ReportsFilterContext = createContext<ReportsFilterContextValue | undefined>(undefined);

export function ReportsFilterProvider({ children }: { children: ReactNode }) {
  const [tenantId, setTenantIdState] = useState<number | null>(null);
  const [lineOfBizId, setLineOfBizIdState] = useState<number | null>(null);
  const [reportTemplateId, setReportTemplateId] = useState<number | null>(null);

  const value = useMemo(
    () => ({
      tenantId,
      lineOfBizId,
      reportTemplateId,
      setTenantId: (id: number | null) => {
        setTenantIdState(id);
        setLineOfBizIdState(null);
        setReportTemplateId(null);
      },
      setLineOfBizId: (id: number | null) => {
        setLineOfBizIdState(id);
        setReportTemplateId(null);
      },
      setReportTemplateId,
    }),
    [tenantId, lineOfBizId, reportTemplateId],
  );

  return <ReportsFilterContext.Provider value={value}>{children}</ReportsFilterContext.Provider>;
}

export function useReportsFilter(): ReportsFilterContextValue {
  const ctx = useContext(ReportsFilterContext);
  if (!ctx) throw new Error('useReportsFilter must be used inside ReportsFilterProvider');
  return ctx;
}
